import React, { useState, useEffect } from 'react';
import type { ReactElement } from 'react';
import { CloudWatch } from 'aws-sdk';
import { ActiveChannels } from '../common/channelsEnum';
import { useParams } from 'react-router-dom';
import Insights from './Insights';
import { api } from '../../utils/axiosConfig';
import { MetricDataResults } from 'aws-sdk/clients/cloudwatch';
import { Notification } from '../../interfaces/api/Notifications';
import moment from 'moment';

const InsightsContainer = (): ReactElement => {
  const { accountId } = useParams<{ accountId: string }>();
  const [notifications, setNotifications] = useState<Notification[]>();
  const [data, setData] = useState<MetricDataResults>();
  const [dateRange, setDateRange] = useState<[moment.Moment, moment.Moment]>([
    moment().subtract(1, 'month'),
    moment()
  ]);
  const [detail, setDetail] = useState<number>(86400);

  useEffect(() => {
    if (notifications) {
      getMetrics(
        accountId,
        notifications.map((n) => n.notificationId),
        dateRange,
        detail
      ).then((res) => {
        setData(res);
      });
    }
  }, [dateRange, accountId, notifications, detail]);

  useEffect(() => {
    getNotifications(accountId).then((res) => {
      setNotifications(res);
    });
  }, [accountId]);

  return (
    <Insights
      data={data ?? []}
      dateRange={dateRange}
      setDateRange={setDateRange}
      detail={detail}
      setDetail={setDetail}
    />
  );
};

const getMetrics = async (
  envId: string,
  notificationIds: string[],
  dateRange: [moment.Moment, moment.Moment],
  detail: number
) => {
  const statusAndNotificationIds: {
    status: string;
    notificationId: string;
  }[] = [];
  // const notificationIdAndChannels: {
  //   channel: ActiveChannels;
  //   notificationId: string;
  // }[] = [];
  const byStatusAndChannels: {
    status: string;
    channel: ActiveChannels;
  }[] = [];

  notificationIds.forEach((notificationId) => {
    ['FAILURE'].forEach((status) => {
      statusAndNotificationIds.push({
        status,
        notificationId
      });
    });

    // Object.values(ActiveChannels).forEach((channel) => {
    //   notificationIdAndChannels.push({
    //     channel,
    //     notificationId
    //   });
    // });
  });

  Object.values(ActiveChannels).forEach((channel) => {
    ['FAILURE'].forEach((status) => {
      byStatusAndChannels.push({
        status,
        channel
      });
    });
  });

  const metrics = [
    // byStatus
    ...['SUCCESS', 'FAILURE'].map((status, i) => {
      return {
        id: `byStatus${i}`,
        name: 'byStatus',
        dimensions: [
          {
            Name: 'status',
            Value: status
          }
        ]
      };
    }),
    // byChannels
    ...Object.values(ActiveChannels).map((channel, i) => {
      return {
        id: `byChannel${i}`,
        name: 'byChannel',
        dimensions: [
          {
            Name: 'channel',
            Value: channel
          }
        ]
      };
    }),
    // byNotificationId
    ...notificationIds.map((notificationId, i) => {
      return {
        id: `byNotificationId${i}`,
        name: 'byNotificationId',
        dimensions: [
          {
            Name: 'notificationId',
            Value: notificationId
          }
        ]
      };
    }),
    // byStatusAndNotificationId
    ...statusAndNotificationIds.map((statusAndNotificationId, i) => {
      return {
        id: `byStatusAndNotificationId${i}`,
        name: 'byStatusAndNotificationId',
        dimensions: [
          {
            Name: 'notificationId',
            Value: statusAndNotificationId.notificationId
          },
          {
            Name: 'status',
            Value: statusAndNotificationId.status
          }
        ]
      };
    }),
    // // notificationIdAndChannel
    // ...notificationIdAndChannels.map((notificationIdAndChannel, i) => {
    //   return {
    //     id: `byNotificationIdAndChannels${i}`,
    //     name: 'byNotificationIdAndChannels',
    //     dimensions: [
    //       {
    //         Name: 'notificationId',
    //         Value: notificationIdAndChannel.notificationId
    //       },
    //       {
    //         Name: 'status',
    //         Value: notificationIdAndChannel.channel
    //       }
    //     ]
    //   };
    // }),
    // bySuccessChannels
    ...byStatusAndChannels.map((statusAndChannel, i) => {
      return {
        id: `bySuccessChannels${i}`,
        name: 'bySuccessChannels',
        dimensions: [
          {
            Name: 'channel',
            Value: statusAndChannel.channel
          },
          {
            Name: 'status',
            Value: statusAndChannel.status
          }
        ]
      };
    })
  ];

  const params: CloudWatch.GetMetricDataInput = {
    MetricDataQueries: metrics.map((m) => {
      return {
        Id: m.id,
        MetricStat: {
          Metric: {
            Namespace: 'Notification Analytics',
            MetricName: `${envId}-${m.name}`,
            Dimensions: m.dimensions
          },
          Period: detail,
          Stat: 'SampleCount'
        }
      };
    }),
    EndTime: dateRange[1].endOf('day').toDate(),
    StartTime: dateRange[0].startOf('day').toDate()
  };
  const res = await api('POST', `${envId}/insights`, params);
  return transformInsightsResponseToMetricDataResults(
    res.data as MetricDataResults
  );
};

// MetricDataResults expects dates to be Date objects,
// but our API returns ISO strings due to json conversion.
// Here, we convert dates to Date objects to comply with the type.
export const transformInsightsResponseToMetricDataResults = (
  data: unknown
): MetricDataResults => {
  return (data as MetricDataResults).map((dataset) => {
    return {
      ...dataset,
      Timestamps: dataset.Timestamps?.map((t) => new Date(t))
    };
  });
};

const getNotifications = async (envId: string) => {
  return (await (
    await api('GET', `${envId}/notifications`)
  ).data) as Notification[];
};

export default InsightsContainer;
